import { useEffect, useRef, useState } from 'react'

import { Box, Button, Group, Paper, Text } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import dayjs from 'dayjs'

import type { TimeRangePreset } from '@/components/ui/TowerDateRangeSelector'
import { TowerDateRangeSelector } from '@/components/ui/TowerDateRangeSelector'

import classes from './NavPageFloatingHeader.module.css'

interface NavPageFloatingHeaderProps {
  selectedDate?: string
  onSelectDate: (date: string) => void
  startDate?: string
  endDate?: string
  onRangeChange?: (startDate: string, endDate: string) => void
  defaultTimePreset?: TimeRangePreset
  // displayMode?: 'chart' | 'table'
  // onDisplayModeChange?: (mode: 'chart' | 'table') => void
}

export function NavPageFloatingHeader({
  selectedDate,
  onSelectDate,
  startDate,
  endDate,
  onRangeChange,
  defaultTimePreset = '1M',
}: NavPageFloatingHeaderProps) {
  const headerRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const [customDate, setCustomDate] = useState<string | null>(null)

  const [localStartDate, setLocalStartDate] = useState<string>(() => {
    if (startDate) return startDate
    return dayjs().subtract(1, 'month').format('YYYY-MM-DD')
  })

  const [localEndDate, setLocalEndDate] = useState<string>(() => {
    if (endDate) return endDate
    return dayjs().format('YYYY-MM-DD')
  })

  const effectiveStartDate = startDate || localStartDate
  const effectiveEndDate = endDate || localEndDate

  // Update placeholder height when header size changes
  useEffect(() => {
    const header = headerRef.current
    const placeholder = placeholderRef.current
    if (!header || !placeholder) return
    placeholder.style.height = `${header.offsetHeight}px`
    const resizeObserver = new ResizeObserver(() => {
      placeholder.style.height = `${header.offsetHeight}px`
    })

    resizeObserver.observe(header)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const handleCustomDateSubmit = () => {
    if (customDate && dayjs(customDate).isValid()) {
      onSelectDate(dayjs(customDate).format('YYYY-MM-DD'))
    }
  }

  const handleRangeChange = (newStartDate: string, newEndDate: string) => {
    if (onRangeChange) {
      onRangeChange(newStartDate, newEndDate)
    } else {
      setLocalStartDate(newStartDate)
      setLocalEndDate(newEndDate)
    }
  }

  return (
    <>
      <div ref={placeholderRef} />

      <Paper ref={headerRef} className={classes.sticky_header} shadow="md">
        <Box className={classes.header_content}>
          <Group className={classes.controls_container}>
            <Group className={classes.date_selector}>
              {selectedDate && (
                <Text>
                  Selected Date: {dayjs(selectedDate).format('MMM D, YYYY')}
                </Text>
              )}
              <DateInput
                value={customDate}
                onChange={setCustomDate}
                placeholder="Select date"
                valueFormat="YYYY-MM-DD"
                size="xs"
                leftSection={<IconCalendar size={14} />}
                clearable
                style={{ width: '140px' }}
              />
              <Button
                size="xs"
                onClick={handleCustomDateSubmit}
                disabled={!customDate}
              >
                View
              </Button>
            </Group>

            <Box className={classes.range_selector}>
              <TowerDateRangeSelector
                startDate={effectiveStartDate}
                endDate={effectiveEndDate}
                onRangeChange={handleRangeChange}
                defaultTimePreset={defaultTimePreset}
                size="xs"
                showPresets={true}
              />
            </Box>
          </Group>
        </Box>
      </Paper>
    </>
  )
}
